<template>
  <AppShell>
    <section class="space-y-8">
      <div class="space-y-3">
        <p class="app-eyebrow">{{ providerLabel }}</p>
        <h1 class="text-3xl font-bold tracking-tight">
          {{ t("purchase.checkoutTitle") }}
        </h1>
        <p class="max-w-2xl text-sm leading-7 text-brand-muted-strong">
          {{ t("purchase.checkoutDescription", { provider: providerLabel }) }}
        </p>
      </div>

      <Card class="rounded-none border-brand-line bg-brand-surface shadow-none">
        <CardContent class="space-y-4 p-8">
          <div
            ref="dropinMount"
            class="min-h-72 border border-brand-line bg-brand-surface p-4"
          >
            <div
              v-if="isLoading"
              class="flex min-h-60 items-center justify-center text-sm font-semibold text-brand-muted"
            >
              <Loader2 class="mr-2 size-4 animate-spin" />
              {{ t("purchase.startingCheckout") }}
            </div>
          </div>

          <p v-if="error" class="text-sm font-medium text-destructive">
            {{ error }}
          </p>
        </CardContent>
      </Card>
    </section>
  </AppShell>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Loader2 } from "lucide-vue-next";
import AppShell from "@/components/AppShell.vue";
import { Card, CardContent } from "@/components/ui/card";
import {
  createPaymentApi,
  usePaymentProvider,
  type PaymentSession,
} from "@/features/payment";
import { useApi } from "@/lib/api";
import { paymentProviderLabel } from "@/lib/config";
import { useI18n } from "@/lib/i18n";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const api = useApi();
const paymentProvider = usePaymentProvider();
const paymentApi = createPaymentApi(api);

const providerLabel = paymentProviderLabel(paymentProvider.name);

const dropinMount = ref<HTMLElement | null>(null);
const isLoading = ref(true);
const error = ref<string | null>(null);

onMounted(async () => {
  const paymentId = routeQueryString(route.query.paymentId);
  const sessionFromState = history.state?.session as PaymentSession | undefined;
  if (!paymentId && !sessionFromState) {
    router.replace({ name: "token-purchase" });
    return;
  }

  try {
    const session = sessionFromState ?? (await paymentApi.getSession(paymentId ?? ""));
    await paymentProvider.startCheckout(session, {
      mountEl: dropinMount.value ?? undefined,
      onComplete: (query) => {
        isLoading.value = false;
        setTimeout(() => {
          router.push({ name: "payment-return", query });
        }, 2000);
      },
      onFailed: (query) => {
        router.push({ name: "payment-return", query });
      },
    });
    isLoading.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : t("purchase.checkoutError");
    isLoading.value = false;
  }
});

function routeQueryString(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) return value;
  if (Array.isArray(value)) return routeQueryString(value[0]);
  return undefined;
}
</script>
